import request from '../../../util/SuperagentUtils';
import type { SuperAgentRequest } from 'superagent';
import { PaymentMethod } from '../../../types/PaymentMethod';

type OnSuccess<T = unknown> = (data: T) => void;
type OnFail = (err: unknown) => void;

interface RenamePaymentMethodData {
    authorityId?: string;
    accountId?: string;
    paymentMethodId: string;
    nickName: string;
}

interface AchSetupIntentData {
    plaidPublicToken: string;
    plaidAccountId: string;
    nickName?: string;
}

interface AchSetupIntentResponse {
    status: string;
    clientSecret: string | null;
    paymentMethod: Partial<PaymentMethod>;
}

interface VerifyBankAccountData {
    paymentMethodId?: string;
    [key: string]: unknown;
}

interface PartnerVerifyBankAccountData {
    fundingSourceId?: string;
    accountId?: string;
    [key: string]: unknown;
}

export const requestPaymentMethods = (
    id: string,
    onSuccess: OnSuccess<PaymentMethod[]>,
    onFail: OnFail,
    excludeExpired?: boolean
): void => {
    requestPaymentMethodsForAccountId(id, onSuccess, onFail, excludeExpired)
}

export const requestPaymentMethodsForAccountId = (
    accountId: string,
    onSuccess: OnSuccess<PaymentMethod[]>,
    onFail: OnFail,
    excludeExpired?: boolean
): void => {
    if (accountId) {
        const queryParams: { accountId: string; excludeExpired?: boolean } = { accountId };
        if (excludeExpired) {
            queryParams.excludeExpired = true;
        }
        request
            .get("/api/payment-method")
            .query(queryParams)
            .then((resp) => onSuccess(resp.body))
            .catch((err) => onFail(err))
    }
}

export const requestDeactivateAccountPaymentMethod = (
    accountId: string,
    paymentMethodId: string,
    onSuccess: OnSuccess,
    onFail: OnFail
): void => {
    if (accountId && paymentMethodId) {
        request
            .post(`/api/payment-method/deactivate`)
            .send({
                id: paymentMethodId,
                accountId
            })
            .then((resp) => onSuccess(resp))
            .catch((err) => onFail(err))
    }
}

export const requestRenamePaymentMethod = (
    data: RenamePaymentMethodData,
    onSuccess: OnSuccess,
    onFail: OnFail
): void => {
    requestRenameAccountPaymentMethod({ ...data, accountId: data.authorityId }, onSuccess, onFail)
}

export const requestRenameAccountPaymentMethod = (
    data: RenamePaymentMethodData,
    onSuccess: OnSuccess,
    onFail: OnFail
): void => {
    const { accountId, paymentMethodId, nickName } = data || {}

    if (accountId && paymentMethodId) {
        request
            .post(`/api/payment-method/rename`)
            .send({
                id: paymentMethodId,
                accountId,
                nickName
            })
            .then((resp) => onSuccess(resp))
            .catch((err) => onFail(err))
    }
}

export const requestSetupIntent = (): Promise<{ clientSecret: string }> => {
    return request
        .post('/api/payment-method/card/setup-intent')
        .then((resp) => resp.body)
}

export const confirmCardPaymentMethod = (
    setupIntentId: string,
    nickName?: string
): Promise<Partial<PaymentMethod>> => {
    return request
        .post('/api/payment-method/card')
        .send({ setupIntentId, nickName })
        .then((resp) => resp.body)
}

export const requestAchSetupIntent = (data: AchSetupIntentData): Promise<AchSetupIntentResponse> => {
    return request
        .post('/api/payment-method/ach/setup-intent')
        .send(data)
        .then((resp) => resp.body)
}

export const confirmAchPaymentMethod = (
    setupIntentId: string,
    nickName?: string
): Promise<Partial<PaymentMethod>> => {
    return request
        .post('/api/payment-method/ach')
        .send({ setupIntentId, nickName })
        .then((resp) => resp.body)
}

type RequestVerifyBankAccount = {
    (data: VerifyBankAccountData): SuperAgentRequest;
    (data: VerifyBankAccountData, onSuccess: OnSuccess<Partial<PaymentMethod>>, onFail: OnFail): void;
};

export const requestVerifyBankAccount = ((
    data: VerifyBankAccountData,
    onSuccess?: OnSuccess<Partial<PaymentMethod>>,
    onFail?: OnFail
): void | SuperAgentRequest => {
    const result = request
        .post(`/api/payment-method/verify`)
        .send(data)

    if (onSuccess) {
        result
            .then((resp) => onSuccess(resp.body))
            .catch((err) => onFail && onFail(err))
    } else {
        return result;
    }
}) as RequestVerifyBankAccount;

type RequestPartnerVerifyBankAccount = {
    (data: PartnerVerifyBankAccountData): SuperAgentRequest;
    (data: PartnerVerifyBankAccountData, onSuccess: OnSuccess<void>, onFail: OnFail): void;
};

export const requestPartnerVerifyBankAccount = ((
    data: PartnerVerifyBankAccountData,
    onSuccess?: OnSuccess<void>,
    onFail?: OnFail
): void | SuperAgentRequest => {
    const result = request
        .post(`/api/payment-method/verify/partner`)
        .send(data)

    if (onSuccess) {
        result
            .then(() => onSuccess())
            .catch((err) => onFail && onFail(err))
    } else {
        return result
    }
}) as RequestPartnerVerifyBankAccount;

type RequestPlaidLinkToken = {
    (): SuperAgentRequest;
    (onSuccess: OnSuccess<string>, onFail: OnFail): void;
};

export const requestPlaidLinkToken = ((
    onSuccess?: OnSuccess<string>,
    onFail?: OnFail
): void | SuperAgentRequest => {
    const result = request
        .post('/api/payment-method/plaid-link-token')
    if (onSuccess && onFail) {
        result
            .then((resp) => onSuccess(resp.body))
            .catch((err) => onFail(err));
    } else {
        return result;
    }
}) as RequestPlaidLinkToken;

// Plaid Link token in update mode for a bank's existing Plaid connection (re-authorization).
export const requestPlaidUpdateLinkToken = (paymentMethodId: string): Promise<string> =>
    request
        .post(`/api/payment-method/${paymentMethodId}/plaid-update-link-token`)
        .then((resp) => resp.text);

export const requestMarkPlaidReauthorized = (paymentMethodId: string): SuperAgentRequest =>
    request.post(`/api/payment-method/${paymentMethodId}/plaid-reauthorized`);

type RequestGetFundingSourceId = {
    (accountId: string): SuperAgentRequest;
    (accountId: string, onSuccess: OnSuccess<string>, onFail: OnFail): void;
};

export const requestGetFundingSourceId = ((
    accountId: string,
    onSuccess?: OnSuccess<string>,
    onFail?: OnFail
): void | SuperAgentRequest => {
    const result = request
        .get(`/api/payment-method/funding-source`)
        .query({ accountId });

    if (onSuccess && onFail) {
        result
            .then((resp) => onSuccess(resp.text))
            .catch((err) => onFail(err));
    } else {
        return result;
    }
}) as RequestGetFundingSourceId;
